import {
  ADMIN_ROLES,
  API_KEY_SCOPE_LABELS,
  API_KEY_SCOPES,
  type ApiKeyScope,
} from '@performance-crm/shared';
import {
  Bot,
  Building2,
  FolderKanban,
  KeyRound,
  ListTree,
  MessageCircle,
  Shuffle,
  Smartphone,
  Target,
  Upload,
  Users,
  Tags,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { TargetsDialog } from '@/components/funnel/TargetsDialog';
import { TemplatesEditor } from '@/components/leads/WhatsappMenu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import { useCompanyScope } from '@/lib/company-context';
import { fmt } from '@/lib/format';
import { currentMonth, monthLabel, monthOptions } from '@/lib/months';
import { useApiKeys, useCreateApiKey, useRevokeApiKey, useRotateApiKey } from '@/lib/queries';
import { CopyButton } from '@/pages/ingest/IngestPage';
import { RulesTab } from '@/pages/TeamPage';
import { CatalogsTab } from './CatalogsTab';
import { CompanyTab } from './CompanyTab';
import { ProjectsTab } from './ProjectsTab';
import { UsersTab } from './UsersTab';
import { WhatsappAccountTab } from './WhatsappAccountTab';
import { CustomizationTab } from './CustomizationTab';
import { BotTab } from './BotTab';
import { UnitsImportTab } from './UnitsImportTab';

type Tab =
  | 'empresa'
  | 'proyectos'
  | 'unidades'
  | 'usuarios'
  | 'catalogos'
  | 'personalizacion'
  | 'bot'
  | 'plantillas'
  | 'whatsapp'
  | 'metas'
  | 'api-keys'
  | 'distribucion';
const TABS: Array<[Tab, string, typeof Building2]> = [
  ['empresa', 'Empresa', Building2],
  ['proyectos', 'Proyectos', FolderKanban],
  ['unidades', 'Unidades (Excel / CSV)', Upload],
  ['usuarios', 'Usuarios', Users],
  ['catalogos', 'Catálogos', ListTree],
  ['personalizacion', 'Etiquetas y campos', Tags],
  ['plantillas', 'Plantillas WhatsApp', MessageCircle],
  ['whatsapp', 'Número de WhatsApp', Smartphone],
  ['bot', 'Bot de atención', Bot],
  ['metas', 'Metas', Target],
  ['api-keys', 'API keys', KeyRound],
  ['distribucion', 'Distribución', Shuffle],
];

/** Administración por empresa (ADMIN / GERENTE): permite operar sin depender del seed. */
export function AdminPage() {
  const { user } = useAuth();
  const { active } = useCompanyScope();
  const [params, setParams] = useSearchParams();
  // Incluye las empresas ocultas: el administrador entra aquí para volver a mostrarlas.
  const adminCompanies = (user?.memberships ?? []).filter(
    (m, i, a) =>
      ADMIN_ROLES.includes(m.role) && a.findIndex((x) => x.companyId === m.companyId) === i,
  );
  const companyId =
    params.get('companyId') ??
    (adminCompanies.some((c) => c.companyId === active) ? active : adminCompanies[0]?.companyId);
  const tab = (params.get('tab') as Tab) ?? 'empresa';
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  if (user && adminCompanies.length === 0) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Administración</h1>
          <p className="text-muted-foreground text-sm">
            Configuración de la empresa, proyectos, unidades, usuarios, catálogos, plantillas,
            metas, API keys y reglas de distribución.
          </p>
        </div>
        {adminCompanies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => set('companyId', e.target.value)}
            aria-label="Empresa"
            className="w-56"
          >
            {adminCompanies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
                {c.companyActive === false ? ' (oculta)' : ''}
              </option>
            ))}
          </Select>
        )}
      </div>
      <div className="bg-card flex flex-wrap gap-1 rounded-md border p-1">
        {TABS.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => set('tab', key)}
            className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm ${tab === key ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            data-testid={`tab-${key}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {companyId && tab === 'empresa' && <CompanyTab companyId={companyId} />}
      {companyId && tab === 'proyectos' && <ProjectsTab companyId={companyId} />}
      {companyId && tab === 'personalizacion' && <CustomizationTab companyId={companyId} />}
      {companyId && tab === 'bot' && <BotTab companyId={companyId} />}
      {companyId && tab === 'unidades' && (
        <UnitsImportTab companyId={companyId} initialProjectId={params.get('projectId') ?? ''} />
      )}
      {companyId && tab === 'usuarios' && <UsersTab companyId={companyId} />}
      {companyId && tab === 'catalogos' && <CatalogsTab companyId={companyId} />}
      {companyId && tab === 'plantillas' && (
        <Card>
          <CardHeader>
            <CardTitle>Plantillas de WhatsApp</CardTitle>
            <CardDescription>
              Marcadores: {'{nombre}'}, {'{proyecto}'}, {'{asesor}'}, {'{empresa}'}. Se usan en el
              botón de WhatsApp de la ficha y en el chat del número propio. Para enviar fuera de las
              24 h por el número real, cada plantilla necesita su nombre aprobado en Meta (allí los
              marcadores son {'{{1}}'}, {'{{2}}'}… en el mismo orden).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TemplatesEditor companyId={companyId} />
          </CardContent>
        </Card>
      )}
      {companyId && tab === 'whatsapp' && <WhatsappAccountTab companyId={companyId} />}
      {companyId && tab === 'metas' && <TargetsTab companyId={companyId} />}
      {companyId && tab === 'api-keys' && <ApiKeysTab companyId={companyId} />}
      {companyId && tab === 'distribucion' && <RulesTab companyId={companyId} />}
    </div>
  );
}

function TargetsTab({ companyId }: { companyId: string }) {
  const [month, setMonth] = useState(currentMonth());
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Metas mensuales por proyecto</CardTitle>
        <CardDescription>
          Captaciones, leads, citas, visitas, separaciones y ventas. El embudo compara el real con
          la meta según el % del mes transcurrido.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          aria-label="Mes"
          className="w-40"
        >
          {/* 18 meses hacia atrás y 3 hacia adelante: las metas del mes siguiente se cargan por adelantado. */}
          {monthOptions(18, 3).map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </Select>
        <Button onClick={() => setOpen(true)} data-testid="btn-targets">
          <Target className="h-4 w-4" /> Editar metas de {monthLabel(month)}
        </Button>
        <TargetsDialog companyId={companyId} month={month} open={open} onOpenChange={setOpen} />
      </CardContent>
    </Card>
  );
}

function ApiKeysTab({ companyId }: { companyId: string }) {
  const { data: keys } = useApiKeys(companyId);
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();
  const rotate = useRotateApiKey();
  const toast = useToast();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([...API_KEY_SCOPES]);
  const [expiresAt, setExpiresAt] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await create.mutateAsync({
        companyId,
        name,
        scopes,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:00`).toISOString() : null,
      });
      setSecret(r.key);
      setName('');
      toast.success('API key creada', 'Cópiala ahora: no se vuelve a mostrar.');
    } catch (err) {
      toast.error('No se pudo crear', (err as Error).message);
    }
  };
  const doRotate = async (id: string) => {
    if (
      !confirm(
        '¿Rotar la clave? La anterior deja de funcionar de inmediato y el bot debe usar la nueva.',
      )
    )
      return;
    try {
      const r = await rotate.mutateAsync(id);
      setSecret(r.key);
      toast.success('Clave rotada', 'Cópiala ahora: no se vuelve a mostrar.');
    } catch (err) {
      toast.error('No se pudo rotar', (err as Error).message);
    }
  };
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Cuentas de servicio (bots / multiagente)</CardTitle>
          <CardDescription>
            Cada clave crea un usuario BOT con rol BOT en la empresa. Se envía en la cabecera{' '}
            <code>X-API-Key</code>. Caduca al año si no indicas otra fecha (máximo 2 años); rota las
            claves periódicamente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="ak-name">Nombre</Label>
                <Input
                  id="ak-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  placeholder="Multiagente WhatsApp"
                  className="w-64"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ak-expires">Caduca el</Label>
                <Input
                  id="ak-expires"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-44"
                />
              </div>
              <Button
                type="submit"
                disabled={create.isPending || scopes.length === 0}
                data-testid="api-key-create"
              >
                <KeyRound className="h-4 w-4" /> Crear clave
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {API_KEY_SCOPES.map((sc) => (
                <label
                  key={sc}
                  className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${scopes.includes(sc) ? 'border-primary bg-primary/10' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(sc)}
                    onChange={() =>
                      setScopes((s) => (s.includes(sc) ? s.filter((x) => x !== sc) : [...s, sc]))
                    }
                    className="h-3 w-3"
                  />
                  {API_KEY_SCOPE_LABELS[sc]}
                </label>
              ))}
            </div>
          </form>
          {secret && (
            <p
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30"
              data-testid="api-key-secret"
            >
              Clave (solo ahora): <code className="font-mono">{secret}</code>{' '}
              <CopyButton value={secret} />
            </p>
          )}
        </CardContent>
      </Card>
      <div className="bg-card overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
            <tr>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Prefijo</th>
              <th className="px-3 py-2">Permisos</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Caduca</th>
              <th className="px-3 py-2">Último uso</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {keys?.map((k) => (
              <tr key={k.id} className="border-t" data-testid="api-key-row">
                <td className="px-3 py-2 font-medium">{k.name}</td>
                <td className="px-3 py-2 font-mono text-xs">pcrm_{k.keyPrefix}_…</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {k.scopes.map((sc) => (
                      <Badge key={sc} tone="outline">
                        {sc}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <Badge tone={k.active ? 'success' : 'muted'}>
                    {k.active
                      ? 'activa'
                      : k.expiresAt && new Date(k.expiresAt) < new Date()
                        ? 'vencida'
                        : 'revocada'}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  {fmt.date(k.expiresAt)}
                  {k.active &&
                    k.expiresAt &&
                    new Date(k.expiresAt).getTime() - Date.now() < 30 * 86_400_000 && (
                      <Badge tone="warning" className="ml-1">
                        pronto
                      </Badge>
                    )}
                </td>
                <td className="px-3 py-2">{k.lastUsedAt ? fmt.ago(k.lastUsedAt) : 'nunca'}</td>
                <td className="px-3 py-2 text-right">
                  {k.active && (
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void doRotate(k.id)}
                        data-testid="api-key-rotate"
                      >
                        Rotar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() =>
                          confirm('¿Revocar esta clave? El bot dejará de autenticarse.') &&
                          revoke.mutate(k.id)
                        }
                      >
                        Revocar
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {keys?.length === 0 && (
              <tr>
                <td colSpan={7} className="text-muted-foreground px-3 py-6 text-center">
                  Aún no hay claves.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
